// STEP 1'S FIELDS AND CARD CHROME (WEB-PARITY-1C, moved out of `Details.tsx` by WEB-PARITY-2A so the new cards in
// `detailsCards.tsx` share them). The app's BrandCard, its rows, and its field kinds; every label is the app's own
// (`detailsWording.ts`), stamped with its id.

import { useEffect, useId, useState, type ReactNode, type RefObject } from "react";
import { formatNumber, parseNumber } from "./detailsRules";
import { words, wordsWith, type DetailsWordingId } from "./detailsWording";
import { sizeWordsWith, withPublicRecordsLabel, type SizeBasisWordingId } from "./sizeBasisWording";

// ── the fields ────────────────────────────────────────────────────────────────────────────────────────────────────────

export type IconName = "pin" | "dollar" | "percent" | "ruler" | "columns";

export type Provenance = { source: string; provider?: string; retrievedAt: string } | null | undefined;

/**
 * WHERE A FIGURE CAME FROM, ON ITS ELEMENT AND NOT ON THE PAGE (WEB-PARITY-1B, DT-5): the app names no provider and no
 * time on this screen, and says "public records"; the source, provider and retrieval time stay on the row for us.
 */
function provenance(fact: Provenance): Record<string, string | undefined> {
  return { "data-source": fact?.source, "data-provider": fact?.provider, "data-retrieved-at": fact?.retrievedAt };
}

export function BrandCard({
  icon,
  titleId,
  footerId,
  accent = false,
  children,
  ...marks
}: {
  icon: IconName;
  titleId: DetailsWordingId;
  footerId?: DetailsWordingId;
  accent?: boolean;
  children: ReactNode;
} & Record<`data-${string}`, string | number | undefined>) {
  return (
    <section className={accent ? "brand-card accent" : "brand-card"} {...marks}>
      <h3 className="brand-card-title">
        <CardIcon name={icon} />
        <Words id={titleId} as="span" />
      </h3>
      <div className="brand-card-body">{children}</div>
      {footerId !== undefined && <Words id={footerId} as="p" className="card-footer" />}
    </section>
  );
}

export function CardIcon({ name }: { name: IconName }) {
  const paths: Record<IconName, string> = {
    pin: "M8 1.5a4.5 4.5 0 0 0-4.5 4.5c0 3.2 4.5 8.5 4.5 8.5s4.5-5.3 4.5-8.5A4.5 4.5 0 0 0 8 1.5Zm0 6.3a1.8 1.8 0 1 1 0-3.6 1.8 1.8 0 0 1 0 3.6Z",
    dollar: "M8 1a7 7 0 1 0 0 14A7 7 0 0 0 8 1Zm.7 10.9V13H7.4v-1.1c-1.2-.2-2-.9-2.1-2h1.3c.1.5.6.9 1.5.9.9 0 1.3-.4 1.3-.9 0-.6-.5-.8-1.6-1.1-1.4-.3-2.3-.8-2.3-2 0-1 .8-1.7 1.9-1.9V3.8h1.3v1.1c1.1.2 1.8.9 1.9 1.9H9.3c-.1-.5-.5-.8-1.2-.8-.8 0-1.2.4-1.2.8 0 .6.6.8 1.6 1 1.5.4 2.3.9 2.3 2.1 0 1.1-.9 1.8-2.1 2Z",
    percent: "M4.5 2.5a2 2 0 1 1 0 4 2 2 0 0 1 0-4Zm7 7a2 2 0 1 1 0 4 2 2 0 0 1 0-4ZM12.3 2.3l1.4 1.4-10 10-1.4-1.4Z",
    ruler: "M1.3 10.6 10.6 1.3l4.1 4.1-9.3 9.3ZM4 9.7l1.1 1.1.7-.7-1.1-1.1Zm2-2 1.6 1.6.7-.7-1.6-1.6Zm2-2 1.1 1.1.7-.7-1.1-1.1Zm2-2 1.6 1.6.7-.7-1.6-1.6Z",
    // The app's `building.columns` (the Jurisdiction card, SubjectPropertyView.swift:2430).
    columns: "M8 1 1.5 4.2V5.5h13V4.2ZM2.5 6.5h2v5.5h-2Zm4.5 0h2v5.5H7Zm4.5 0h2v5.5h-2ZM1.5 13h13v1.8h-13Z",
  };
  return (
    <svg className="card-icon" width="13" height="13" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
      <path d={paths[name]} fill="currentColor" />
    </svg>
  );
}

export function LockedRow({ labelId, value, field, fact }: { labelId: DetailsWordingId; value: string; field?: string; fact?: Provenance }) {
  return (
    <div className="field-row locked" data-field={field} data-locked="true" {...provenance(fact)}>
      <span className="field-label">
        <Words id={labelId} as="span" />
      </span>
      <span className="field-value">{value}</span>
      <svg className="lock" width="12" height="12" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
        <path d="M4.5 7V5a3.5 3.5 0 1 1 7 0v2h.5A1.5 1.5 0 0 1 13.5 8.5v5A1.5 1.5 0 0 1 12 15H4a1.5 1.5 0 0 1-1.5-1.5v-5A1.5 1.5 0 0 1 4 7Zm1.6 0h3.8V5a1.9 1.9 0 1 0-3.8 0Z" fill="currentColor" />
      </svg>
    </div>
  );
}

export function TextField({
  labelId,
  value,
  placeholderId,
  onCommit,
  field,
  fact,
}: {
  labelId: DetailsWordingId;
  value: string;
  placeholderId?: DetailsWordingId;
  onCommit: (typed: string) => void;
  field: string;
  fact?: Provenance;
}) {
  const id = useId();
  const [text, setText] = useState(value);
  const [editing, setEditing] = useState(false);
  useEffect(() => {
    if (!editing) setText(value);
  }, [value, editing]);
  return (
    <div className="field-row" data-field={field} {...provenance(fact)}>
      <label className="field-label" htmlFor={id} data-held-copy={labelId}>
        {words(labelId)}
      </label>
      <span className="field-input">
        <input
          id={id}
          type="text"
          autoComplete="off"
          spellCheck={false}
          value={text}
          placeholder={placeholderId === undefined ? undefined : words(placeholderId)}
          data-held-copy-placeholder={placeholderId}
          onFocus={() => setEditing(true)}
          onChange={(e) => setText(e.currentTarget.value)}
          onBlur={() => {
            setEditing(false);
            if (text.trim() !== value.trim()) onCommit(text);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") e.currentTarget.blur();
          }}
        />
      </span>
    </div>
  );
}

export function NumberField({
  labelId,
  value,
  onCommit,
  field,
  fact,
  prefix,
  unitId,
  placeholderId,
  whole = false,
  plain = false,
  inputRef,
}: {
  labelId: DetailsWordingId;
  value: number | null;
  onCommit: (typed: number | null) => void;
  field: string;
  fact?: Provenance;
  prefix?: string;
  unitId?: DetailsWordingId;
  placeholderId?: DetailsWordingId;
  /** Whole numbers only (dollars, a year). */
  whole?: boolean;
  /** No thousands separator (a year). */
  plain?: boolean;
  inputRef?: RefObject<HTMLInputElement | null>;
}) {
  const id = useId();
  const shown = value === null ? "" : formatNumber(value, plain);
  const [text, setText] = useState(shown);
  const [editing, setEditing] = useState(false);
  useEffect(() => {
    if (!editing) setText(shown);
  }, [shown, editing]);
  return (
    <div className="field-row" data-field={field} {...provenance(fact)}>
      <label className="field-label" htmlFor={id} data-held-copy={labelId}>
        {words(labelId)}
      </label>
      <span className="field-input">
        {prefix !== undefined && <span className="affix">{prefix}</span>}
        <input
          id={id}
          ref={inputRef}
          type="text"
          inputMode={whole ? "numeric" : "decimal"}
          autoComplete="off"
          value={text}
          placeholder={placeholderId === undefined ? undefined : words(placeholderId)}
          onFocus={() => setEditing(true)}
          onChange={(e) => setText(e.currentTarget.value)}
          onBlur={() => {
            setEditing(false);
            const parsed = parseNumber(text, whole);
            if (parsed === "invalid") {
              setText(shown);
              return;
            }
            if (parsed !== value) onCommit(parsed);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") e.currentTarget.blur();
          }}
        />
        {unitId !== undefined && (
          <span className="affix" data-held-copy={unitId}>
            {words(unitId)}
          </span>
        )}
      </span>
    </div>
  );
}

export function SelectField({
  labelId,
  value,
  options,
  onChange,
  field,
  fact,
}: {
  labelId: DetailsWordingId;
  value: string;
  options: readonly { value: string; label: string; wordsId?: string }[];
  onChange: (picked: string) => void;
  field: string;
  fact?: Provenance;
}) {
  const id = useId();
  return (
    <div className="field-row" data-field={field} {...provenance(fact)}>
      <label className="field-label" htmlFor={id} data-held-copy={labelId}>
        {words(labelId)}
      </label>
      <span className="field-input">
        <select id={id} value={value} onChange={(e) => onChange(e.currentTarget.value)}>
          <option value="" data-held-copy="details.select">
            {words("details.select")}
          </option>
          {options.map((o) => (
            <option key={o.value} value={o.value} data-held-copy={o.wordsId}>
              {o.label}
            </option>
          ))}
        </select>
      </span>
    </div>
  );
}

/** One of the app's sentences, stamped with its id — as every sentence this client holds is. */
export function Words({
  id,
  as: Tag,
  className,
  values,
  publicRecords,
}: {
  id: DetailsWordingId;
  as: "h1" | "h3" | "p" | "span" | "b";
  className?: string;
  values?: Readonly<Record<string, string | number>>;
  /** R-pending-size-label (L1), Tyler's sentence 3: the server said the record's figure is a data provider's, so the
   *  app's "County record:" reads "Public records:" (`sizeBasisWording.ts`). Only the two size-correction sentences
   *  take it. */
  publicRecords?: boolean;
}) {
  const text = values === undefined ? words(id) : wordsWith(id, values);
  return publicRecords === true ? (
    <Tag className={className} data-held-copy={id} data-size-label="publicRecords">
      {withPublicRecordsLabel(text)}
    </Tag>
  ) : (
    <Tag className={className} data-held-copy={id}>
      {text}
    </Tag>
  );
}

/** One of Tyler's approved size sentences (R-pending-size-label, L1; `sizeBasisWording.ts`), said only where the server's
 *  `SubjectView.sqftWording` chose it. */
export function SizeWords({
  id,
  className,
  values,
}: {
  id: SizeBasisWordingId;
  className?: string;
  values: Readonly<Record<string, string | number>>;
}) {
  return (
    <p className={className} data-held-copy={id}>
      {sizeWordsWith(id, values)}
    </p>
  );
}

// ── WEB-PARITY-2A's fields ───────────────────────────────────────────────────────────────────────────────────────────

/** A calendar day, `YYYY-MM-DD` — the app's compact `DatePicker` (the deadline, SubjectPropertyView.swift:2456; a sale's
 *  date, CompEditView.swift:146). Committed when it changes to a whole day; emptied, it is no date. */
export function DateField({
  labelId,
  value,
  onCommit,
  field,
}: {
  labelId: DetailsWordingId;
  value: string | null;
  onCommit: (day: string | null) => void;
  field: string;
}) {
  const id = useId();
  const [text, setText] = useState(value ?? "");
  const [editing, setEditing] = useState(false);
  useEffect(() => {
    if (!editing) setText(value ?? "");
  }, [value, editing]);
  const commit = (typed: string): void => {
    const day = /^\d{4}-\d{2}-\d{2}$/.test(typed) ? typed : typed === "" ? null : undefined;
    if (day === undefined) {
      setText(value ?? "");
      return;
    }
    if (day !== value) onCommit(day);
  };
  return (
    <div className="field-row" data-field={field}>
      <label className="field-label" htmlFor={id} data-held-copy={labelId}>
        {words(labelId)}
      </label>
      <span className="field-input">
        <input
          id={id}
          type="date"
          value={text}
          onFocus={() => setEditing(true)}
          onChange={(e) => setText(e.currentTarget.value)}
          onBlur={(e) => {
            setEditing(false);
            commit(e.currentTarget.value);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") e.currentTarget.blur();
          }}
        />
      </span>
    </div>
  );
}

/** The app's `Toggle` (the "Filing deadline" switch, SubjectPropertyView.swift:2449-2453), as a checkbox styled a switch. */
export function ToggleField({
  labelId,
  on,
  onChange,
  field,
}: {
  labelId: DetailsWordingId;
  on: boolean;
  onChange: (on: boolean) => void;
  field: string;
}) {
  const id = useId();
  return (
    <div className="field-row toggle-row" data-field={field}>
      <label className="field-label" htmlFor={id} data-held-copy={labelId}>
        {words(labelId)}
      </label>
      <span className="field-input">
        <input id={id} type="checkbox" role="switch" className="switch" checked={on} onChange={(e) => onChange(e.currentTarget.checked)} />
      </span>
    </div>
  );
}

/** The app's tax-year `Stepper` (SubjectPropertyView.swift:1869-1875), bounded as the app bounds it: five years back to one
 *  ahead. The browser's own number stepper, so no word is added for its buttons. */
export function YearStepper({
  labelId,
  helperId,
  value,
  min,
  max,
  onCommit,
}: {
  labelId: DetailsWordingId;
  helperId: DetailsWordingId;
  value: number;
  min: number;
  max: number;
  onCommit: (year: number) => void;
}) {
  const id = useId();
  const [text, setText] = useState(String(value));
  useEffect(() => setText(String(value)), [value]);
  const commit = (): void => {
    const n = Number(text);
    if (!Number.isInteger(n)) {
      setText(String(value));
      return;
    }
    const clamped = Math.min(Math.max(n, min), max);
    setText(String(clamped));
    if (clamped !== value) onCommit(clamped);
  };
  return (
    <div className="field-row stepper-row" data-field="taxYear">
      <span className="field-label">
        <label htmlFor={id} data-held-copy={labelId} className="stepper-label">
          {words(labelId)}
        </label>
        <Words id={helperId} as="span" className="stepper-helper" />
      </span>
      <span className="field-input">
        <input
          id={id}
          type="number"
          inputMode="numeric"
          min={min}
          max={max}
          step={1}
          value={text}
          onChange={(e) => setText(e.currentTarget.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === "Enter") e.currentTarget.blur();
          }}
        />
      </span>
    </div>
  );
}
